import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom'
import { SITE } from '../data/site.js'
import { cart } from '../lib/cart.js'
import Icon from './Icon.jsx'
import Logo from './Logo.jsx'

// The shop links live in a "Shop" dropdown: flat, they need ~860px of navbar and
// the container only has ~1140px once the logo and icons are in, which squeezed the
// wordmark onto two lines. Collapsed, the bar fits comfortably and new categories can
// be added without breaking it.
const SHOP_NAV = [
  { to: '/shop', label: 'Shop All' },
  { to: '/shop/shower-screens', label: 'Shower Screens' },
  { to: '/shop/hardware', label: 'Hardware' },
  { to: '/hardware', label: 'Hardware by Finish' },
  { to: '/shop/glass-panels', label: '10mm Frameless Glass Panels' },
  { to: '/shop/tools-accessories', label: 'Tools & Accessories' },
  { to: '/shop/wardrobe-doors', label: 'Wardrobe Doors' },
]

const NAV = [
  { to: '/gallery', label: 'Gallery' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
]

export default function Header() {
  const [count, setCount] = useState(cart.count())
  const [open, setOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [term, setTerm] = useState('')
  const [shopOpen, setShopOpen] = useState(false)
  const shopRef = useRef(null)
  const navigate = useNavigate()
  const { pathname } = useLocation()
  useEffect(() => cart.subscribe(() => setCount(cart.count())), [])

  // close the Shop dropdown on outside click, on Escape, and whenever the route changes
  useEffect(() => { setShopOpen(false); setOpen(false) }, [pathname])
  useEffect(() => {
    if (!shopOpen) return
    const onDown = (e) => { if (!shopRef.current?.contains(e.target)) setShopOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setShopOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey) }
  }, [shopOpen])

  function submitSearch(e) {
    e.preventDefault()
    const q = term.trim()
    if (!q) return
    setSearchOpen(false)
    setOpen(false)
    setTerm('')
    navigate(`/shop?q=${encodeURIComponent(q)}`)
  }

  return (
    <header className="header">
      <div className="topbar">
        <div className="container topbar-inner">
          <span className="topbar-item topbar-promo"><Icon name="truck" size={15} /> Local delivery (50 km) · free warehouse pickup</span>
          <span className="topbar-spacer" />
          <a className="topbar-item" href={SITE.phoneHref}><Icon name="phone" size={14} /> {SITE.phone}</a>
          <a className="topbar-item topbar-email" href={`mailto:${SITE.email}`}><Icon name="mail" size={14} /> {SITE.email}</a>
          <span className="topbar-item topbar-hours"><Icon name="clock" size={14} /> {SITE.hours}</span>
        </div>
      </div>
      <div className="navbar">
        <div className="container navbar-inner">
          <Link to="/" className="brand" aria-label="Level Up Glass Supply — home">
            <Logo size={40} />
          </Link>
          <nav className={`nav ${open ? 'nav-open' : ''}`} aria-label="Main navigation">
            <div className={`nav-group ${shopOpen ? 'open' : ''}`} ref={shopRef}>
              <button
                type="button"
                className={`nav-trigger ${pathname.startsWith('/shop') || pathname.startsWith('/hardware') ? 'active' : ''}`}
                aria-expanded={shopOpen}
                aria-haspopup="true"
                onClick={() => setShopOpen((o) => !o)}
              >
                Shop <Icon name="chevronDown" size={15} className={shopOpen ? 'flip' : ''} />
              </button>
              <div className="nav-menu">
                {SHOP_NAV.map((item) => (
                  <NavLink key={item.to} to={item.to} end={item.to === '/shop'} onClick={() => { setShopOpen(false); setOpen(false) }}>
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </div>
            {NAV.map((item) => (
              <NavLink key={item.to} to={item.to} onClick={() => setOpen(false)}>
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="navbar-actions">
            <button
              className={`cart-button ${searchOpen ? 'active' : ''}`}
              aria-label="Search products"
              aria-expanded={searchOpen}
              onClick={() => setSearchOpen((o) => !o)}
            >
              <Icon name={searchOpen ? 'x' : 'search'} size={22} />
            </button>
            <Link to="/account" className="cart-button" aria-label="My account">
              <Icon name="user" size={22} />
            </Link>
            <Link to="/cart" className="cart-button" aria-label={`Cart, ${count} items`}>
              <Icon name="cart" size={22} />
              {count > 0 && <span className="cart-badge">{count}</span>}
            </Link>
            <button className="menu-button" onClick={() => setOpen(!open)} aria-label="Toggle menu">
              <Icon name={open ? 'x' : 'menu'} size={24} />
            </button>
          </div>
        </div>
      </div>
      {searchOpen && (
        <div className="search-bar">
          <form className="container search-bar-inner" onSubmit={submitSearch} role="search">
            <Icon name="search" size={18} />
            <input
              autoFocus
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setSearchOpen(false) }}
              placeholder="Search products — e.g. shower screen, hinge, wardrobe…"
              aria-label="Search products"
            />
            <button className="btn btn-primary" type="submit">Search</button>
          </form>
        </div>
      )}
    </header>
  )
}
